import { compact, some } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { useLocation } from 'react-router-dom';

import { useCustomerInfo } from '@/api/customers';
import { Invoice } from '@/api/invoices';
import { createPaymentInvoice, useUpdatePaymentInvoice } from '@/api/payments';
import { BACK_TARGETS, TransactionContext } from '@/context/transaction/common';
import {
    TransactionBackTarget,
    TransactionContextProviderCommonProps,
    TransactionContextValue,
} from '@/context/transaction/types';
import { useNow } from '@/hooks/useNow';

export type PaymentTransactionContextProviderProps = {
    type: 'payment';
    notFromAccountBalance?: boolean;
} & TransactionContextProviderCommonProps;

export const PaymentTransactionContextProvider = ({
    totalDueLabel = 'Total due now',
    notFromAccountBalance,
    defaultBackTarget,
    children,
}: PaymentTransactionContextProviderProps) => {
    const mixpanel = useMixpanel();
    const { data: customerInfo, isPending: customerPending } = useCustomerInfo();
    const [invoice, setInvoice] = useState<Invoice>();
    const invoiceId = invoice?.id;

    const [paymentMethod, setPaymentMethod] = useState<string>();
    const [quantities, setQuantities] = useState<{ [price_id: string]: number }>({});
    const { mutateAsync: updatePaymentInvoice, isPending: updateMutationPending } = useUpdatePaymentInvoice({
        invoiceId,
    });
    const now = useNow(300);
    const location = useLocation();
    const backTarget = (location.state?.transactionBackTarget as TransactionBackTarget) ?? defaultBackTarget;

    useEffect(() => {
        createPaymentInvoice().then((response) => {
            setInvoice(response.data.invoice);
        });
    }, []);

    const items = useMemo(
        () => compact(Object.entries(quantities).map(([price, quantity]) => (quantity ? { price, quantity } : null))),
        [quantities]
    );

    useEffect(() => {
        if (!invoiceId) return;

        updatePaymentInvoice({
            items,
        }).then((response) => {
            setInvoice(response.data.invoice);
        });
    }, [quantities, invoiceId, items, updatePaymentInvoice]);

    const amountDue = useMemo(() => {
        // amount due on the invoice is the total less the amount coming out of account balance. if we're not allowing payment from account balance,
        // we'll say the full total is due, _unless_ it's less than amount_due, which would indicate that they already owe us some money, in which case
        // we're going to charge them the full amount due (cover their negative balance plus whatever they're trying to pay for now.)
        if (!invoice) {
            return null;
        }
        if (notFromAccountBalance) {
            return Math.max(invoice.total, invoice.amount_due);
        }
        return invoice.amount_due;
    }, [invoice, notFromAccountBalance]);

    const hasPayment = useMemo(() => {
        if ((amountDue ?? 0) <= 0) {
            return true;
        }

        return Boolean(paymentMethod || customerInfo?.data.default_payment_method);
    }, [amountDue, paymentMethod, customerInfo?.data.default_payment_method]);

    const contextValue: TransactionContextValue = useMemo(() => {
        const hasChanges = some(quantities, (qty) => !!qty);

        const commonContext = {
            type: 'payment',
            totalDueLabel,
            backTarget: BACK_TARGETS[backTarget],
            track: (eventName: string, properties?: object) =>
                mixpanel?.track(eventName, {
                    type: 'payment',
                    invoiceId,
                    hasPayment,
                    hasChanges,
                    totalDue: invoice?.total,
                    ...properties,
                }),
        } as const;

        if (customerPending || !invoice || !invoiceId) {
            return { ...commonContext, isPending: true, isUpdating: true, hasChanges: false, hasPayment: false };
        }

        return {
            ...commonContext,
            isPending: false,
            isUpdating: updateMutationPending,
            currency: customerInfo?.data.currency,
            billingDate: now,
            totalDue: invoice.total,
            paymentDue: amountDue!,
            lineItemQuantities: { ...quantities },
            setLineItemQuantity(price_id, quantity) {
                setQuantities((quantities) => {
                    if (quantities[price_id] === quantity) {
                        return quantities;
                    }
                    return { ...quantities, [price_id]: quantity };
                });
            },
            paymentMethod,
            setPaymentMethod,
            hasChanges,
            hasPayment,
            invoice,
            notFromAccountBalance: !!notFromAccountBalance,
        };
    }, [
        quantities,
        totalDueLabel,
        backTarget,
        customerPending,
        invoice,
        invoiceId,
        updateMutationPending,
        customerInfo?.data.currency,
        now,
        amountDue,
        paymentMethod,
        hasPayment,
        notFromAccountBalance,
        mixpanel,
    ]);

    return <TransactionContext.Provider value={contextValue}>{children}</TransactionContext.Provider>;
};
